/**
 * File navigation.js.
 *
 * Handles toggling the navigation menu for small screens and enables TAB key
 * navigation support for dropdown menus.
 */
var first_tee_menu = (function () {
    /**
     * @typedef DropDownMenu
     * @type {{container: HTMLElement, toggle: (HTMLElement|null), menu: (HTMLElement|null)}}
     */
    var mainMenu = {
        container: document.getElementById('masthead'),
        menu: null,
        toggle: null
    };
    mainMenu.menu = document.getElementById('main-menu');
    mainMenu.toggle = readFirstChildOfName(mainMenu.container, 'menu-toggle');

    /*
     * CSS Class Constants
     */

    var MENU_TOGGLE_CLASS = 'toggled';
    var SUBMENU_FOCUS_CLASS = 'focus';
    var SUBMENU_FOCUS_REVEAL_CLASS = 'focus-reveal';
    var SUBMENU_ITEM_FOCUS_CLASS = 'focus';

    /**
     * @returns {HTMLSpanElement}
     */
    function buildSubmenuToggleElement() {
        var screenReaderText = document.createElement('span'),
            submenuToggle = document.createElement('span');
        screenReaderText.classList.add('screen-reader-text');
        screenReaderText.innerText = '(activate to toggle sub menu)';
        submenuToggle.classList.add('caret', 'caret-down');
        submenuToggle.append(screenReaderText);
        return submenuToggle;

    }

    /**
     * @param {DropDownMenu} menu
     */
    function checkMenuExists(menu) {

        return menu && menu.container && menu.menu && 0 < menu.menu.children.length && menu.toggle;
    }

    /**
     * Copied from Underscore
     *
     * @param {(function(): void)} func
     * @param {number} wait
     * @param {boolean} immediate
     * @returns {(function(): void)|*}
     */
    function debounce(func, wait, immediate) {
        var timeout;
        return function () {
            var context = this,
                args = arguments,
                later = function () {
                    timeout = null;
                    if (!immediate) {
                        func.apply(context, args);
                    }
                },
                callNow = immediate && !timeout;

            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
            if (callNow) {
                func.apply(context, args);
            }
        };
    }

    /**
     *
     * @param {DropDownMenu} menu
     */
    function disableMenu(menu) {
        if (menu.toggle) {
            menu.toggle.hidden = true;
        }
    }

    /**
     * @param {HTMLElement} element
     * @param {string} className
     */
    function elementEnsureClass(element, className) {
        if (!element.classList.contains(className)) {
            element.classList.add(className);
        }
    }

    /**
     * @param {HTMLAnchorElement} link
     * @return boolean
     */
    function isLinkToggleOnly(link) {
        var uriRemoveQueryString = link.href ? link.href.split('?') : [''],
            pathSegments = uriRemoveQueryString[0].split('/');

        return 0 < pathSegments.length && '#' === pathSegments[pathSegments.length - 1];
    }

    /**
     * @param {HTMLElement|null} element
     * @param {string} tagName
     * @returns {HTMLElement|null}
     */
    function readFirstChildOfName(element, tagName) {
        var children = element ? element.getElementsByClassName(tagName) : new HTMLCollection();
        return 0 < children.length ? children[0] : null;
    }

    /**
     * @param {HTMLElement} submenuItem
     * @param {HTMLElement} targetElement
     */
    function registerSubmenuFocusHandlers(submenuItem, targetElement) {
        targetElement.addEventListener('focus', function () {
            elementEnsureClass(submenuItem, SUBMENU_FOCUS_CLASS);
        });
        targetElement.addEventListener('blur', function () {
            submenuItem.classList.remove(SUBMENU_FOCUS_CLASS);
        });
    }

    /**
     * @param {HTMLElement|null} element
     * @param {boolean} nextState
     */
    function setAriaExpanded(element, nextState) {
        element ? element.setAttribute('aria-expanded', nextState ? 'true' : 'false') : null;
    }

    /**
     * Configure aria and CSS menu elements for theme
     * @param {DropDownMenu} menu
     */
    function setupMenu(menu) {
        var submenus = menu.menu.children,
            submenuIndex;

        setAriaExpanded(menu.menu, false);
        elementEnsureClass(menu.menu, 'nav-menu');
        setupMenuToggle(menu);

        for (submenuIndex = 0; submenuIndex < submenus.length; submenuIndex++) {
            setupSubmenus(submenus[submenuIndex]);
        }
    }

    /**
     * Hide the menu toggle/trigger button if the menu is not present
     *
     * @param {DropDownMenu} menu
     */
    function setupMenuToggle(menu) {
        menu.toggle.addEventListener('click', function () {
            var menu_container = document.getElementsByClassName('header__bottom')[0];
            var nextState = !menu_container.classList.contains(MENU_TOGGLE_CLASS);
            menu_container.classList.toggle(MENU_TOGGLE_CLASS);
            setAriaExpanded(menu.toggle, nextState);
            setAriaExpanded(menu.menu, nextState);
        });
    }

    /**
     * @param {HTMLElement} currentSubmenu
     */
    function setupSubmenus(currentSubmenu) {
        var menuLink = null,
            submenu = null,
            child,
            index;

        for (index = 0; index < currentSubmenu.children.length; index++) {
            child = currentSubmenu.children.item(index);

            if (null === menuLink && 'a' === child.tagName.toLowerCase()) {
                menuLink = child;
            }
            if (null === submenu && child.classList.contains('sub-menu')) {
                submenu = child;
            }
        }

        if (submenu) {
            registerSubmenuFocusHandlers(currentSubmenu, menuLink);
            setupSubmenuToggle(currentSubmenu, menuLink);
            trackSubmenuLinkFocus(currentSubmenu)
        }
    }

    /**
     * Setup the submenu toggle based on whether the link goes to other content
     *  If it does not got to other content, the entire link target toggles the submenu,
     *  otherwise only the toggle element toggles the submenu
     *
     * @param {HTMLElement} menuItem
     * @param {HTMLAnchorElement} link
     */
    function setupSubmenuToggle(menuItem, link) {
        var toggle = buildSubmenuToggleElement(),
            toggleTarget = isLinkToggleOnly(link) ? link : toggle,
            toggleRevealEvent = debounce(
                function () {
                    elementEnsureClass(menuItem, SUBMENU_FOCUS_CLASS);
                    menuItem.classList.toggle(SUBMENU_FOCUS_REVEAL_CLASS);
                },
                250,
                false);

        registerSubmenuFocusHandlers(menuItem, toggle);
        toggleTarget.setAttribute('tabindex', '0');
        toggleTarget.addEventListener('click', function (event) {
            event.preventDefault()
            toggleRevealEvent();
        });
        toggleTarget.addEventListener('touchstart', function (event) {
            event.preventDefault()
            toggleRevealEvent();
        });
        toggleTarget.addEventListener('keydown', function (event) {
            if (13 === event.which) {
                event.preventDefault()
                toggleRevealEvent();
            }
        });

        link.append(toggle);
    }

    /**
     * Focus and Blur event handling for submenu link items
     *  - Tracks focus state from each menu item link to the containing menu item
     *  - Tracks focus state from each menu item link to the containing submenu
     *  - IE11 compatability requires this approach, otherwise use focus-within
     * @param {HTMLElement} submenu
     */
    function trackSubmenuLinkFocus(submenu) {
        var submenuItems = submenu.getElementsByClassName('menu-item'),
            linkIndex,
            link;

        for (linkIndex = 0; linkIndex < submenuItems.length; linkIndex++) {
            link = submenuItems[linkIndex].getElementsByTagName('a')[0];
            if (link) {
                registerSubmenuFocusHandlers(submenu, link);
                link.addEventListener('focus', (function (currentItem) {
                    return function () {
                        elementEnsureClass(currentItem, SUBMENU_ITEM_FOCUS_CLASS);
                    }
                })(submenuItems[linkIndex]));
                link.addEventListener('blur', (function (currentItem) {
                    return function () {
                        currentItem.classList.remove(SUBMENU_ITEM_FOCUS_CLASS);
                    }
                })(submenuItems[linkIndex]));
            }
        }
    }

    /**
     * Singleton initialization latch, avoid duplicate registration
     *
     * @type {boolean}
     * @private
     */
    var initialized = false;

    return {
        initialize: function () {
            if (!initialized) {
                checkMenuExists(mainMenu) ? setupMenu(mainMenu) : disableMenu(mainMenu);
                initialized = true;
            }

        }
    }
})();

first_tee_menu.initialize();


(function ($) {

    $('header a, .footer-menu a, .legal-menu a').each(function() {
        var a = new RegExp('/' + window.location.host + '/');
        if(!a.test(this.href)) {
			$(this).attr({
                rel: "noopener nofollow",
                'aria-label': "Opens in a new window",
                target: "_blank"
            });
        }
    });

})(window.jQuery);
